import {
  encodePolyline,
  formatDistanceMeters,
  formatDurationSeconds,
  type RouteDetail,
  type RouteGravel,
  type SegmentMode,
} from '@motogo/shared';
import { ArrowLeftRight, BookmarkPlus, Check, Plus, RotateCcw, Trash2, Undo2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';

import PlaceSearch, { shortPlaceName } from './PlaceSearch';

import { useAuth } from '@/hooks/useAuth';
import { useDiscover } from '@/hooks/useDiscover';
import { MAX_VERBATIM_POINTS, MAX_WAYPOINTS, useRouteEditor } from '@/hooks/useRouteEditor';
import {
  buildLoadedRouteSaveInput,
  loadedRouteCoordinates,
  loadedRouteDurationSeconds,
  useRoutePlan,
} from '@/hooks/useRoutePlan';
import { useSavedRouteDetail } from '@/hooks/useSavedRouteDetail';
import { useSettings } from '@/hooks/useSettings';
import { putLocalRoute } from '@/lib/routeStore';
import { saveRoute, updateRouteFull } from '@/lib/savedRoutes';

const MODES: { value: SegmentMode; label: string }[] = [
  { value: 'fast', label: 'Fast' },
  { value: 'scenic', label: 'Scenic' },
  { value: 'curvy', label: 'Curvy' },
  { value: 'straight', label: 'Straight' },
];

const GRAVEL: { value: RouteGravel; label: string }[] = [
  { value: 'exclude', label: 'Avoid' },
  { value: 'neutral', label: 'Allow' },
  { value: 'include', label: 'Seek' },
];

type SaveStatus = 'closed' | 'editing' | 'saving';

/**
 * M3 waypoint route editor — /routes/new and /routes/:id/edit. The map (behind this
 * transparent overlay) is the editing surface; this panel carries the chips, totals,
 * selection actions and save flow. Editing an existing route hydrates from its stored
 * polyline + plan with zero routing calls; only segments the user changes re-route.
 */
const RouteEditorPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const editor = useRouteEditor();
  const { settings } = useSettings();
  const { positionRef, activeRoute, loadedRoute } = useRoutePlan();
  // The generic map-jump bus (lives on the discover provider) — a searched waypoint
  // should put the map where the point landed, not leave it wherever it was panned.
  const { flyTo } = useDiscover();
  const { state: authState, exitGuestMode } = useAuth();
  const isGuest = authState.status === 'guest';

  // Only meaningful for /routes/:id/edit; for /routes/new the id is undefined and the
  // hook settles on 'notfound', which the hydration effect ignores.
  const { state: detailState } = useSavedRouteDetail(id);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('closed');
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Guard the dirty session against a lost-work navigation. Unlike the planner (whose route
  // survives in context), the editor's unmount cleanup wipes the session — so the hardware
  // back button, the browser back gesture and the header ✕ would all silently discard a
  // route the rider spent minutes placing. useBlocker (needs the data router) intercepts any
  // navigation while dirty and shows a confirm; a save sets bypassRef so it isn't blocked.
  const dirtyRef = useRef(false);
  dirtyRef.current = editor.dirty;
  const bypassRef = useRef(false);
  const blocker = useBlocker(useCallback(() => dirtyRef.current && !bypassRef.current, []));

  // Opening the editor fresh while a route is already on the map: editing that route is
  // at least as likely as sketching from scratch, so ask instead of assuming. Decided at
  // mount — a saved route jumps to its own edit URL, an unsaved one hydrates in place.
  const [needsChoice, setNeedsChoice] = useState(() => !isEdit && !!(activeRoute || loadedRoute));
  // Default save name when the session came from an unsaved route on the map.
  const [fallbackName, setFallbackName] = useState('Custom route');

  const editCurrentRoute = () => {
    if (activeRoute) {
      // The edit URL runs the full machinery: ownership check, roundTrip → planner
      // prefill, plan/track hydration from the mirror or server.
      navigate(`/routes/${activeRoute.id}/edit`, { replace: true });
      return;
    }
    if (loadedRoute) {
      const coords = loadedRouteCoordinates(loadedRoute);
      // Planner results rebuild their plan recipe (so an A→B keeps its mode); a GPX
      // carries the track plan built from its waypoints; inline shares are plan-less.
      const plan =
        loadedRoute.kind === 'planned'
          ? (buildLoadedRouteSaveInput(loadedRoute, loadedRoute.name)?.plan ?? null)
          : loadedRoute.kind === 'gpx'
            ? (loadedRoute.plan ?? null)
            : null;
      if (
        editor.startFromGeometry(
          coords,
          plan,
          loadedRouteDurationSeconds(loadedRoute),
          settings.routeMode,
          settings.routeGravel,
        )
      ) {
        setFallbackName(loadedRoute.name);
      }
    }
    // Session live (or nothing usable after all — the effect below starts blank).
    setNeedsChoice(false);
  };

  // ── Session lifecycle ─────────────────────────────────────────────────────
  // Gated on `editor.active` rather than a ref: the unmount cleanup stops the session,
  // so under StrictMode's mount→cleanup→mount the second pass simply starts it again.
  useEffect(() => {
    if (editor.active) return;
    if (!isEdit) {
      if (needsChoice) return; // the edit-current / start-new chooser is up
      // Seed the first segment's chips from the planner prefs — same riding taste.
      editor.startNew(settings.routeMode, settings.routeGravel);
      return;
    }
    if (detailState.status === 'loading') return;
    if (detailState.status !== 'ready') {
      navigate('/routes', { replace: true });
      return;
    }
    const route: RouteDetail = detailState.route;
    if (!(route.owned ?? true) || route.isFrozen) {
      navigate(`/routes/${route.id}`, { replace: true });
      return;
    }
    if (route.plan?.kind === 'roundTrip') {
      // The editor can't reproduce GraphHopper's round_trip algorithm — the planner,
      // pre-filled from the plan snapshot, is the re-edit surface for these.
      navigate('/route', {
        replace: true,
        state: {
          prefill: {
            editRouteId: route.id,
            name: route.name,
            description: route.description,
            mode: route.plan.mode,
            gravel: route.plan.gravel,
            distanceKm: route.plan.distanceKm,
            headingDegrees: route.plan.headingDegrees,
          },
        },
      });
      return;
    }
    if (!editor.startFromRoute(route, settings.routeMode, settings.routeGravel)) {
      // Plan doesn't fit the stored geometry (shouldn't happen — server-validated).
      navigate(`/routes/${route.id}`, { replace: true });
    }
    // editor actions/settings are stable or seed-only; detailState is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailState, isEdit, editor.active, needsChoice]);

  // Leaving the editor (save, back, hardware back) always tears the session down.
  useEffect(() => () => editor.stop(), [editor.stop]); // eslint-disable-line react-hooks/exhaustive-deps

  // The ✕ just navigates; the blocker above turns a dirty exit into a confirm, so back
  // button and ✕ share one path.
  const close = () => navigate(isEdit ? `/routes/${id}` : '/routes');

  // ── Selection context ─────────────────────────────────────────────────────
  const selection = editor.selection;
  const selectedSegmentIndex =
    selection?.kind === 'segment' ? editor.segments.findIndex((s) => s.id === selection.id) : -1;
  const selectedSegment = selectedSegmentIndex >= 0 ? editor.segments[selectedSegmentIndex] : null;
  const selectedWaypointIndex =
    selection?.kind === 'waypoint' ? editor.waypoints.findIndex((w) => w.id === selection.id) : -1;
  const selectedWaypoint = selectedWaypointIndex >= 0 ? editor.waypoints[selectedWaypointIndex] : null;

  // Chips edit the selected segment when there is one, otherwise the next segment to add.
  const chipMode = selectedSegment ? selectedSegment.mode : editor.nextMode;
  const chipGravel = selectedSegment ? selectedSegment.gravel : editor.nextGravel;
  const pickMode = (mode: SegmentMode) =>
    selectedSegment ? editor.setSegmentMode(selectedSegment.id, mode) : editor.setNextMode(mode);
  const pickGravel = (gravel: RouteGravel) =>
    selectedSegment ? editor.setSegmentGravel(selectedSegment.id, gravel) : editor.setNextGravel(gravel);

  const errorSegments = editor.segments.filter((s) => s.status === 'error');

  // ── Save ──────────────────────────────────────────────────────────────────
  const openSave = () => {
    setSaveName(editor.editing?.name ?? fallbackName);
    setSaveError(null);
    setSaveStatus('editing');
  };

  /** Saves the session: PUT onto the edited route, or (asNew / fresh session) POST a new one. */
  const handleSave = async (asNew: boolean) => {
    const name = saveName.trim();
    const result = editor.buildResult();
    if (!name || !result) return;
    if (result.coordinates.length > MAX_VERBATIM_POINTS) {
      setSaveError('This route is too detailed to save — shorten it or use fewer waypoints.');
      return;
    }
    const creatingNew = asNew || !editor.editing;
    const routeId = crypto.randomUUID();
    setSaveStatus('saving');
    setSaveError(null);
    try {
      if (!creatingNew && editor.editing) {
        const updated = await updateRouteFull(editor.editing.id, {
          name,
          description: editor.editing.description,
          coordinates: result.coordinates,
          durationSeconds: result.durationSeconds,
          plan: result.plan,
        });
        if (!updated) throw new Error('The route could not be updated — sign in and try again.');
        await putLocalRoute(updated, true).catch(() => undefined);
        bypassRef.current = true; // a save is not a discard — don't let the blocker intercept
        navigate(`/routes/${updated.id}`);
        return;
      }

      const saved = await saveRoute({
        id: routeId,
        name,
        source: 'editor',
        coordinates: result.coordinates,
        durationSeconds: result.durationSeconds,
        plan: result.plan,
      });
      if (!saved) {
        setSaveStatus('editing');
        setSaveError('Sign in to save routes.');
        return;
      }
      await putLocalRoute(saved, true).catch(() => undefined);
      bypassRef.current = true;
      navigate(`/routes/${saved.id}`);
    } catch (e) {
      if (creatingNew) {
        // Offline (or server hiccup) on a new route — queue in the mirror like the
        // planner does; the library's sync pass retries with the same client GUID, so
        // an ambiguous failure can't duplicate.
        const now = new Date().toISOString();
        const latLon = result.coordinates.map(([lon, lat]) => [lat, lon] as [number, number]);
        const local: RouteDetail = {
          id: routeId,
          name,
          description: null,
          source: 'editor',
          visibility: 'private',
          distanceMeters: result.distanceMeters,
          durationSeconds: result.durationSeconds,
          overviewPolyline: encodePolyline(latLon), // server replaces with the simplified one on sync
          polyline: encodePolyline(latLon),
          plan: result.plan,
          createdAt: now,
          updatedAt: now,
          ownerName: null,
        };
        try {
          await putLocalRoute(local, false);
          bypassRef.current = true;
          navigate(`/routes/${local.id}`);
          return;
        } catch {
          /* mirror unavailable too — fall through to the error message */
        }
      }
      setSaveStatus('editing');
      setSaveError(e instanceof Error ? e.message : 'Saving failed');
    }
  };

  const title = editor.editing ? editor.editing.name : needsChoice ? 'Route editor' : 'New route';
  const waitingForRoute = isEdit && !editor.active;

  return (
    <div className="mg-editor">
      {blocker.state === 'blocked' && (
        <div className="mg-confirm-backdrop" role="dialog" aria-modal="true" aria-label="Discard route?">
          <div className="mg-confirm-card">
            <p className="mg-confirm-text">Discard this route? Your unsaved changes will be lost.</p>
            <div className="mg-confirm-actions">
              <button className="mg-btn mg-btn-outline" onClick={() => blocker.reset?.()} autoFocus>
                Keep editing
              </button>
              <button className="mg-btn mg-btn-danger" onClick={() => blocker.proceed?.()}>
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="mg-editor-header">
        <button className="mg-icon-btn" onClick={close} aria-label="Close editor" title="Close editor">
          <X size={26} />
        </button>
        <h1 className="mg-editor-title">{title}</h1>
        <button
          className="mg-icon-btn"
          onClick={editor.reverse}
          disabled={editor.waypoints.length < 2}
          style={{ opacity: editor.waypoints.length >= 2 ? 1 : 0.35 }}
          aria-label="Reverse direction"
          title="Reverse direction"
        >
          <ArrowLeftRight size={24} />
        </button>
        <button
          className="mg-icon-btn"
          onClick={editor.undo}
          disabled={!editor.canUndo}
          style={{ opacity: editor.canUndo ? 1 : 0.35 }}
          aria-label="Undo"
          title="Undo"
        >
          <Undo2 size={24} />
        </button>
      </header>

      <div className="mg-editor-panel">
        {!isEdit && needsChoice ? (
          <>
            <div className="mg-plan-hint">
              There&apos;s a route on the map — edit it, or start from scratch?
            </div>
            <button className="mg-btn mg-btn-primary" onClick={editCurrentRoute}>
              Edit route on map
            </button>
            <button
              className="mg-btn mg-btn-outline"
              onClick={() => setNeedsChoice(false)}
            >
              Start a new route
            </button>
          </>
        ) : waitingForRoute ? (
          <div className="mg-plan-hint">Loading route…</div>
        ) : (
          <>
            {saveStatus === 'closed' && editor.waypoints.length < MAX_WAYPOINTS && (
              <PlaceSearch
                placeholder={editor.waypoints.length === 0 ? 'Search a start point' : 'Add a waypoint by search'}
                selected={null}
                onSelect={(place) => {
                  if (!place) return;
                  editor.addWaypoint(place.lat, place.lon, shortPlaceName(place.name));
                  flyTo(place.lat, place.lon, 13);
                }}
                biasRef={positionRef}
              />
            )}
            {editor.waypoints.length >= MAX_WAYPOINTS && (
              <div className="mg-plan-hint">Waypoint limit reached ({MAX_WAYPOINTS}).</div>
            )}

            {/* Selection context: a tapped segment or waypoint, else the next segment to add */}
            {selectedWaypoint ? (
              <div className="mg-editor-row">
                <span className="mg-setting-label">
                  Waypoint {selectedWaypointIndex + 1}
                  {selectedWaypoint.name ? ` · ${selectedWaypoint.name}` : ''}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="mg-btn mg-btn-danger"
                    style={{ width: 'auto', padding: '8px 14px' }}
                    onClick={() => editor.removeWaypoint(selectedWaypoint.id)}
                  >
                    <Trash2 size={18} /> Remove
                  </button>
                  <button className="mg-icon-btn" onClick={() => editor.select(null)} aria-label="Deselect" title="Deselect">
                    <X size={20} />
                  </button>
                </div>
              </div>
            ) : (
              editor.waypoints.length > 0 && (
                <div className="mg-editor-row">
                  <span className="mg-setting-label">
                    {selectedSegment
                      ? `Segment ${selectedSegmentIndex + 1} · ${
                          selectedSegment.status === 'error'
                            ? 'Routing failed'
                            : formatDistanceMeters(selectedSegment.distanceMeters)
                        }`
                      : 'Next segment'}
                  </span>
                  {selectedSegment && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {selectedSegment.status === 'error' && (
                        <button
                          className="mg-btn mg-btn-outline"
                          style={{ width: 'auto', padding: '8px 14px' }}
                          onClick={() => editor.retrySegment(selectedSegment.id)}
                        >
                          <RotateCcw size={18} /> Retry
                        </button>
                      )}
                      {editor.waypoints.length < MAX_WAYPOINTS && (
                        <button
                          className="mg-btn mg-btn-outline"
                          style={{ width: 'auto', padding: '8px 14px' }}
                          onClick={() => editor.insertVia(selectedSegment.id)}
                        >
                          <Plus size={18} /> Via here
                        </button>
                      )}
                      <button className="mg-icon-btn" onClick={() => editor.select(null)} aria-label="Deselect" title="Deselect">
                        <X size={20} />
                      </button>
                    </div>
                  )}
                </div>
              )
            )}

            {editor.waypoints.length > 0 && (
              <>
                <div className="mg-segment mg-plan-segment">
                  {/* "Track" appears only as the selected track segment's current state —
                      picking any routed mode converts (re-routes) that stretch; nothing
                      can be converted *to* a track (its line comes from stored geometry). */}
                  {chipMode === 'track' && <button className="mg-segment-btn active">Track</button>}
                  {MODES.map((m) => (
                    <button
                      key={m.value}
                      className={chipMode === m.value ? 'mg-segment-btn active' : 'mg-segment-btn'}
                      onClick={() => pickMode(m.value)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {chipMode !== 'straight' && chipMode !== 'track' && (
                  <div className="mg-setting-row" style={{ minHeight: 0 }}>
                    <span className="mg-setting-label">Gravel</span>
                    <div className="mg-segment">
                      {GRAVEL.map((g) => (
                        <button
                          key={g.value}
                          className={chipGravel === g.value ? 'mg-segment-btn active' : 'mg-segment-btn'}
                          onClick={() => pickGravel(g.value)}
                        >
                          {g.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {editor.waypoints.length >= 2 && (
              <div className="mg-plan-summary">
                {formatDistanceMeters(editor.totalDistanceMeters)} · {formatDurationSeconds(editor.totalDurationSeconds)}
                {editor.busy && <span className="mg-editor-busy"> · Calculating…</span>}
              </div>
            )}

            {errorSegments.length > 0 && (
              <div className="mg-plan-error">
                Routing failed for {errorSegments.length === 1 ? 'a segment' : `${errorSegments.length} segments`}.{' '}
                <button className="mg-link-btn" onClick={() => errorSegments.forEach((s) => editor.retrySegment(s.id))}>
                  Retry
                </button>
              </div>
            )}

            {saveError && <div className="mg-plan-error">{saveError}</div>}

            {/* Save flow — guests can sketch but a route saves to the account library */}
            {editor.canSave &&
              (isGuest ? (
                <button className="mg-btn mg-btn-outline" onClick={exitGuestMode}>
                  Sign in to save this route
                </button>
              ) : saveStatus === 'closed' ? (
                <button className="mg-btn mg-btn-primary" onClick={openSave}>
                  <BookmarkPlus size={20} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
                  {editor.editing ? 'Save changes' : 'Save to library'}
                </button>
              ) : editor.editing ? (
                // Editing an existing route: update it in place, or keep it and save a copy.
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    className="mg-input"
                    value={saveName}
                    maxLength={120}
                    placeholder="Route name"
                    onChange={(e) => setSaveName(e.target.value)}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="mg-btn mg-btn-primary"
                      style={{ flex: 1 }}
                      disabled={saveStatus === 'saving' || !saveName.trim()}
                      onClick={() => void handleSave(false)}
                    >
                      {saveStatus === 'saving' ? (
                        'Saving…'
                      ) : (
                        <>
                          <Check size={18} /> Update route
                        </>
                      )}
                    </button>
                    <button
                      className="mg-btn mg-btn-outline"
                      style={{ flex: 1 }}
                      disabled={saveStatus === 'saving' || !saveName.trim()}
                      onClick={() => void handleSave(true)}
                    >
                      Save as new
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="mg-input"
                    style={{ flex: 1 }}
                    value={saveName}
                    maxLength={120}
                    placeholder="Route name"
                    onChange={(e) => setSaveName(e.target.value)}
                  />
                  <button
                    className="mg-btn mg-btn-primary"
                    style={{ width: 'auto', padding: '0 18px' }}
                    disabled={saveStatus === 'saving' || !saveName.trim()}
                    onClick={() => void handleSave(false)}
                  >
                    {saveStatus === 'saving' ? (
                      'Saving…'
                    ) : (
                      <>
                        <Check size={18} /> Save
                      </>
                    )}
                  </button>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default RouteEditorPage;
